import { MarkdownNativeTemplateEditor } from '@/components/tiptap/markdown-native/template-editor';
import { useMarkdownNativeEditor } from '@/components/tiptap/markdown-native/use-markdown-native-editor';
import { isEditorLive } from '@/components/tiptap/shared/safe-editor';
import { EditorType } from '@/components/tiptap/types/index.enums';
import { type TemplateEditorTabViewProps } from '@heidi/growth';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Markdown-native onboarding template editor: the stored markdown hydrates the
 * editor directly and `getMarkdown()` serializes it back, so the onboarding
 * review surface renders lists and H2–H5 content headings the same way the
 * template builder and the preview do.
 */
export function TemplateEditorTabMarkdownNative({
  content,
  onContentChange,
  tabType,
  placeholder,
  isEditable = false,
}: TemplateEditorTabViewProps) {
  const { editor } = useMarkdownNativeEditor({
    content,
    contentType: 'markdown',
    editable: isEditable,
    placeholder,
    editorType: EditorType.TEMPLATE,
    surface: 'template',
  });

  const editorRef = useRef<HTMLDivElement>(null);

  const handleOnUpdate = useCallback(() => {
    if (!onContentChange) return;
    if (!isEditorLive(editor, 'TemplateEditorTabMarkdownNative.handleOnUpdate')) return;

    // Onboarding has no both-tabs clear rule — an emptied editor writes back the
    // empty string, which is what the legacy path's serializer produced too.
    onContentChange(editor.isEmpty ? '' : editor.getMarkdown());
  }, [editor, onContentChange]);

  useEffect(() => {
    if (!editor) return;

    editor.on('update', handleOnUpdate);
    return () => {
      editor.off('update', handleOnUpdate);
    };
  }, [editor, handleOnUpdate]);

  if (!editor) {
    return (
      <div className="flex size-full items-center justify-center">
        <div className="size-6 animate-spin rounded-full border-2 border-border border-t-text-primary" />
      </div>
    );
  }

  return (
    <div className="size-full overflow-y-auto pr-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <MarkdownNativeTemplateEditor
        editor={editor}
        ref={editorRef}
        testTag={`onboarding-${tabType}-block-editor`}
        className="!overflow-visible"
      />
    </div>
  );
}
